import type { PasskeyAssertion, PasskeyCreationOptions, PasskeyRegistration, PasskeyRequestOptions } from '@bms2/shared';

/**
 * The browser's half of a passkey (piece 21; decision 0351): the API speaks
 * base64url JSON and the WebAuthn API speaks ArrayBuffers, so the two
 * ceremonies convert at this edge and nowhere else. 🔨 Nothing here decides
 * whether a passkey signs anyone in; the API verifies the answer against the
 * challenge the password earned.
 */
export function bytesToBase64Url(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = '';
  for (const byte of view) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function base64UrlToBytes(text: string): Uint8Array {
  const padded = text.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (text.length % 4)) % 4);
  const binary = atob(padded);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

/** Whether this browser offers passkeys at all; the screens hide the offer otherwise. */
export function passkeysSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function' && typeof navigator.credentials?.create === 'function';
}

interface CredentialLike {
  id: string;
  rawId: ArrayBuffer;
  response: { clientDataJSON: ArrayBuffer; attestationObject?: ArrayBuffer; authenticatorData?: ArrayBuffer; signature?: ArrayBuffer; userHandle?: ArrayBuffer | null };
}

/** navigator.credentials.create over the API's options; null when the person cancelled or the browser refused. */
export async function createPasskey(options: PasskeyCreationOptions): Promise<PasskeyRegistration | null> {
  const publicKey = {
    ...options,
    challenge: base64UrlToBytes(options.challenge),
    user: { ...options.user, id: base64UrlToBytes(options.user.id) },
    excludeCredentials: options.excludeCredentials.map((c) => ({ ...c, id: base64UrlToBytes(c.id) })),
  } as unknown as PublicKeyCredentialCreationOptions;
  const credential = (await navigator.credentials.create({ publicKey }).catch(/* silent: a passkey the person cancels or the browser refuses is a refusal, answered on the page. */ () => null)) as CredentialLike | null;
  if (!credential?.response.attestationObject) return null;
  return { id: credential.id, rawId: bytesToBase64Url(credential.rawId), response: { clientDataJSON: bytesToBase64Url(credential.response.clientDataJSON), attestationObject: bytesToBase64Url(credential.response.attestationObject) } };
}

/** navigator.credentials.get over the API's request options; null when the person cancelled or the browser refused. */
export async function getPasskey(options: PasskeyRequestOptions): Promise<PasskeyAssertion | null> {
  const publicKey = {
    ...options,
    challenge: base64UrlToBytes(options.challenge),
    allowCredentials: options.allowCredentials.map((c) => ({ ...c, id: base64UrlToBytes(c.id) })),
  } as unknown as PublicKeyCredentialRequestOptions;
  const credential = (await navigator.credentials.get({ publicKey }).catch(/* silent: a passkey the person cancels or the browser refuses is a refusal, answered on the page. */ () => null)) as CredentialLike | null;
  const response = credential?.response;
  if (!credential || !response?.authenticatorData || !response.signature) return null;
  return {
    id: credential.id,
    rawId: bytesToBase64Url(credential.rawId),
    response: {
      clientDataJSON: bytesToBase64Url(response.clientDataJSON),
      authenticatorData: bytesToBase64Url(response.authenticatorData),
      signature: bytesToBase64Url(response.signature),
      userHandle: response.userHandle ? bytesToBase64Url(response.userHandle) : null,
    },
  };
}
